import {
    Container,
    Stack,
    ToggleButton,
    ToggleButtonOption,
    ToggleButtonOptionGroup,
    Typography,
} from "@octopus-energy/coral/atoms"
import { useTranslations } from "next-intl"
import { useFeatureFlag } from "@/contexts/FeatureFlagsProvider"
import { InvoiceUploadNewChip } from "@/features/invoiceUpload"
import { InputMethodSchema } from "@/features/subscription/meterEligibility/schemas/inputMethodSchema"
import { type InputMethod } from "@/features/subscription/meterEligibility/types/inputMethod"
import { getOnboardingConfig } from "@/features/subscription/utils/getOnboardingConfig"
import { isElectricityOnly } from "@/features/subscription/utils/isMonoEnergy"
import { useInitialOnboardingFormContext } from "../../context/InitialOnboardingFormContext"

export function InputMethodToggle() {
    const t = useTranslations("InputMethodToggle")
    const isInvoiceUploadEnabled = useFeatureFlag("invoiceUpload")
    const { inputMethod, journeyType, selectedEnergyTypes, onboardingState, setInputMethod } =
        useInitialOnboardingFormContext()

    const handleMethodChange = (_: React.MouseEvent<HTMLElement>, value: InputMethod) => {
        const parsed = InputMethodSchema.safeParse(value)

        if (parsed.success) {
            setInputMethod(parsed.data)
        }
    }

    if (journeyType === "COMMISSIONING") {
        return null
    }

    const { invoiceUploadEnabled } = getOnboardingConfig({
        salesChannel: onboardingState.formValues.affiliateOrganisationSalesChannel,
        affiliateCode: onboardingState.formValues.affiliateCode,
        affiliateOrganisation: onboardingState.formValues.affiliateOrganisationName,
    })

    const hasInvoiceUpload =
        isInvoiceUploadEnabled &&
        invoiceUploadEnabled &&
        journeyType === "CHANGE_OF_SUPPLIER" &&
        isElectricityOnly(selectedEnergyTypes)

    return (
        <>
            <Typography textStyle="h4">{t("title")}</Typography>
            <ToggleButton
                fullWidth
                value={inputMethod}
                onChange={handleMethodChange}
            >
                <Container
                    position="relative"
                    fullWidth
                >
                    {hasInvoiceUpload && <InvoiceUploadNewChip />}
                    <ToggleButtonOptionGroup>
                        <Stack gap="xxs">
                            <ToggleButtonOption
                                value="addressLookup"
                                attributes={{ "data-testid": "addressLookupOption" }}
                            >
                                {t("addressLookup")}
                            </ToggleButtonOption>
                            <ToggleButtonOption
                                value="meterPoint"
                                attributes={{ "data-testid": "meterPointOption" }}
                            >
                                {t("meterPoint")}
                            </ToggleButtonOption>
                            {hasInvoiceUpload && (
                                <ToggleButtonOption
                                    value="invoice"
                                    attributes={{ "data-testid": "invoiceOption" }}
                                >
                                    {t("invoice")}
                                </ToggleButtonOption>
                            )}
                        </Stack>
                    </ToggleButtonOptionGroup>
                </Container>
            </ToggleButton>
        </>
    )
}

InputMethodToggle.messages = ["InputMethodToggle", ...InvoiceUploadNewChip.messages] as const
